import { g } from '@hcie/core';
import { SelectionMode, Point, CanvasLike } from '@hcie/shared';

/**
 * @file mask-logic.ts
 * @description Core math and pixel-level logic for selection masks.
 * Tracing, shape filling, and mask combination.
 */

const width = () => (typeof g !== "undefined" ? g.image_width : 0);
const height = () => (typeof g !== "undefined" ? g.image_height : 0);

/**
 * Get outline path for marching ants: array of {x,y}.
 */
export function getRectBorder(x1: number, y1: number, x2: number, y2: number) {
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const right = Math.max(x1, x2);
    const bottom = Math.max(y1, y2);
    return [
        { x: left, y: top },
        { x: right, y: top },
        { x: right, y: bottom },
        { x: left, y: bottom },
        { x: left, y: top }
    ];
}

export function getEllipseBorder(x1: number, y1: number, x2: number, y2: number, steps?: number) {
    steps = steps || 64;
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const rx = Math.abs(x2 - x1) / 2;
    const ry = Math.abs(y2 - y1) / 2;
    const out = [];
    for (let i = 0; i <= steps; i++) {
        const t = (i / steps) * Math.PI * 2;
        out.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) });
    }
    return out;
}

/**
 * Create a new mask canvas (same size as document) with alpha=255 inside shape, 0 outside.
 */
export function fillShapeMask(ctx: CanvasRenderingContext2D, shape: string, x1: number, y1: number, x2: number, y2: number, lassoPoints?: Array<{x:number,y:number}>) {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(255,255,255,1)";
    ctx.beginPath();
    if (shape === "rect") {
        const left = Math.min(x1, x2);
        const top = Math.min(y1, y2);
        const right = Math.max(x1, x2);
        const bottom = Math.max(y1, y2);
        ctx.rect(left, top, right - left, bottom - top);
    } else if (shape === "ellipse") {
        const cx = (x1 + x2) / 2;
        const cy = (y1 + y2) / 2;
        const rx = Math.abs(x2 - x1) / 2;
        const ry = Math.abs(y2 - y1) / 2;
        if (rx < 0.5 && ry < 0.5) {
            ctx.rect(Math.floor(cx), Math.floor(cy), 1, 1);
        } else {
            ctx.ellipse(cx, cy, Math.max(0.5, rx), Math.max(0.5, ry), 0, 0, Math.PI * 2);
        }
    } else if (shape === "circle") {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const side = Math.max(Math.abs(dx), Math.abs(dy));
        const nx2 = x1 + (dx < 0 ? -side : side);
        const ny2 = y1 + (dy < 0 ? -side : side);
        const cx = (x1 + nx2) / 2;
        const cy = (y1 + ny2) / 2;
        const radius = side / 2;
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    } else if (shape === "row") {
        ctx.rect(0, y1, w, 1);
    } else if (shape === "column") {
        ctx.rect(x1, 0, 1, h);
    } else if ((shape === "lasso" || shape === "poly") && lassoPoints && lassoPoints.length >= 2) {
        ctx.moveTo(lassoPoints[0].x, lassoPoints[0].y);
        for (let i = 1; i < lassoPoints.length; i++) {
            ctx.lineTo(lassoPoints[i].x, lassoPoints[i].y);
        }
        ctx.closePath();
    }
    ctx.fill();
}

/**
 * Combine current selection with a new shape mask. Returns the final mask canvas.
 */
export function combineMask(newMaskCanvas: HTMLCanvasElement, mode: SelectionMode | 'xor'): HTMLCanvasElement {
    const w = newMaskCanvas.width;
    const h = newMaskCanvas.height;
    if (mode === "replace" || !g.isSelectionActive || !g.selectionCanvas) {
        const finalCtx = newMaskCanvas.getContext("2d");
        if (finalCtx) {
            g.selectionMask = finalCtx.getImageData(0, 0, w, h);
        }
        return newMaskCanvas;
    }

    const resultCanvas = g.createCanvas(w, h) as HTMLCanvasElement;
    const rctx = resultCanvas.getContext("2d");
    if (!rctx) return newMaskCanvas;

    const source = (g as any)._selectionBaseCanvas || g.selectionCanvas;
    if (source) rctx.drawImage(source as HTMLCanvasElement, 0, 0);

    if (mode === "add") {
        rctx.globalCompositeOperation = "source-over";
        rctx.drawImage(newMaskCanvas, 0, 0);
    } else if (mode === "subtract") {
        rctx.globalCompositeOperation = "destination-out";
        rctx.drawImage(newMaskCanvas, 0, 0);
    } else if (mode === "intersect") {
        rctx.globalCompositeOperation = "destination-in";
        rctx.drawImage(newMaskCanvas, 0, 0);
    } else if (mode === "xor") {
        const existing = rctx.getImageData(0, 0, w, h);
        const newCtx = newMaskCanvas.getContext("2d");
        if (newCtx) {
            const newData = newCtx.getImageData(0, 0, w, h);
            for (let i = 3; i < existing.data.length; i += 4) {
                existing.data[i] = (existing.data[i] > 127 !== newData.data[i] > 127) ? 255 : 0;
            }
            rctx.putImageData(existing, 0, 0);
        }
    }

    const finalCtx = resultCanvas.getContext("2d");
    if (finalCtx) {
        g.selectionMask = finalCtx.getImageData(0, 0, w, h);
    }

    return resultCanvas;
}

/**
 * Finds the bounding box of a selection mask.
 */
export function getMaskBounds(maskCanvas: CanvasLike) {
    const w = maskCanvas.width;
    const h = maskCanvas.height;
    const ctx = (maskCanvas as HTMLCanvasElement).getContext("2d", { willReadFrequently: true }) || (maskCanvas as OffscreenCanvas).getContext("2d");
    if (!ctx) return null;
    const data = ctx.getImageData(0, 0, w, h).data;
    let minX = w, minY = h, maxX = 0, maxY = 0;
    let found = false;
    for (let y = 0; y < h; y++) {
        const rowOffset = y * w * 4;
        for (let x = 0; x < w; x++) {
            if (data[rowOffset + x * 4 + 3] > 10) { 
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
                found = true;
            }
        }
    }
    if (!found) return null;
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/**
 * Extracts boundaries from the selection mask using Moore-Neighbor tracing.
 */
export function extractSelectionBorder(maskCanvas: CanvasLike): Point[][] {
    if (!maskCanvas) return [];
    const w = maskCanvas.width;
    const h = maskCanvas.height;
    const ctx = maskCanvas.getContext("2d");
    if (!ctx) return [];
    const bounds = getMaskBounds(maskCanvas);
    if (!bounds) return [];

    const imgData = ctx.getImageData(0, 0, w, h);
    const data = imgData.data;

    const visited = new Uint8Array(w * h);
    const paths: Point[][] = [];

    const isSolid = (x: number, y: number) => {
        if (x < 0 || x >= w || y < 0 || y >= h) return false;
        return data[(y * w + x) * 4 + 3] > 127;
    };

    for (let y = Math.max(0, bounds.y - 1); y <= Math.min(h - 1, bounds.y + bounds.h); y++) {
        for (let x = Math.max(0, bounds.x - 1); x <= Math.min(w - 1, bounds.x + bounds.w); x++) {
            if (!visited[y * w + x] && isSolid(x, y) && (!isSolid(x - 1, y) || !isSolid(x + 1, y) || !isSolid(x, y - 1) || !isSolid(x, y + 1))) {
                const path = traceContour(x, y, w, h, isSolid, visited);
                if (path.length > 5) {
                    paths.push(path);
                }
            }
        }
    }
    return paths;
}

function traceContour(startX: number, startY: number, w: number, h: number, isSolid: (x: number, y: number) => boolean, visited: Uint8Array): Point[] {
    visited[startY * w + startX] = 1;
    const path: Point[] = [{ x: startX, y: startY }];
    let currX = startX;
    let currY = startY;

    const dx = [-1, 0, 1, 1, 1, 0, -1, -1];
    const dy = [-1, -1, -1, 0, 1, 1, 1, 0];
    let moveDir = 7; 
    let exitCount = 0;
    const maxExit = w * h;

    while (exitCount < maxExit) {
        exitCount++;
        let nextFound = false;
        const entryDir = (moveDir + 4) % 8;
        const searchStart = (entryDir + 1) % 8;

        for (let i = 0; i < 8; i++) {
            const checkDir = (searchStart + i) % 8;
            const nx = currX + dx[checkDir];
            const ny = currY + dy[checkDir];

            if (isSolid(nx, ny)) {
                currX = nx;
                currY = ny;
                moveDir = checkDir;
                nextFound = true;
                break;
            }
        }
        if (!nextFound || (currX === startX && currY === startY && path.length > 1)) break;
        path.push({ x: currX, y: currY });
        visited[currY * w + currX] = 1;
    }
    if (path.length > 0) path.push({ x: path[0].x, y: path[0].y });
    return path;
}
